import API_BASE, { withApiCredentials } from "../api";
import { isCanonicalPushInstallationId } from "./pushInstallation";

const STABLE_ERROR_CODES = new Set([
  "push_registration_unavailable",
  "push_registration_installation_invalid",
  "push_registration_fid_invalid",
  "push_registration_invalid",
  "push_registration_platform_invalid",
  "push_registration_context_invalid",
  "push_registration_permission_required",
  "push_registration_locale_invalid",
  "push_registration_timezone_invalid",
  "push_consent_invalid",
  "push_registration_mutation_invalid",
  "push_registration_mutation_stale",
  "csrf_token_invalid",
]);
const PLATFORMS = new Set(["web", "twa"]);
const APP_CONTEXTS = new Set(["browser", "standalone", "twa"]);
const PERMISSION_STATES = new Set(["default", "granted", "denied"]);
const OPTIONAL_METADATA_FIELDS = [
  "locale",
  "timezone",
  "appVersion",
  "deviceLabel",
  "lastRegisteredAt",
  "lastSeenAt",
  "createdAt",
];
const ACTIVE_LOCALES = new Set(["en", "bg", "el", "es", "fr", "pt", "pl", "de", "tr"]);
const FID_PATTERN = /^[cdef][A-Za-z0-9_-]{21}$/;
const CLAIM_METADATA_FIELDS = new Set([
  "platform",
  "appContext",
  "permissionState",
  "locale",
  "timezone",
  "appVersion",
  "deviceLabel",
]);
export const PUSH_ONBOARDING_CONSENT_VERSION = "reelitpro_push_onboarding_v1";
export const PUSH_ONBOARDING_OPT_IN_SOURCE = "onboarding";

export class PushRegistrationsApiError extends Error {
  constructor(code, { operation, status } = {}) {
    super(code);
    this.name = "PushRegistrationsApiError";
    this.code = code;
    this.operation = operation;
    this.status = status;
  }
}

export function isValidFirebaseInstallationId(value) {
  return typeof value === "string" && FID_PATTERN.test(value);
}

function authorizationHeaders(authToken) {
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
}

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isSafeCount(value) {
  return Number.isInteger(value) && value >= 0;
}

function sanitizeRegistration(value) {
  if (
    !isObject(value) ||
    !isCanonicalPushInstallationId(value.installationId) ||
    value.provider !== "fcm" ||
    !PLATFORMS.has(value.platform) ||
    !APP_CONTEXTS.has(value.appContext) ||
    !PERMISSION_STATES.has(value.permissionState) ||
    typeof value.enabled !== "boolean" ||
    !isCanonicalPushInstallationId(value.currentDeviceReference) ||
    (value.refreshRequired !== undefined &&
      typeof value.refreshRequired !== "boolean") ||
    (value.providerTargetInvalidated !== undefined &&
      typeof value.providerTargetInvalidated !== "boolean")
  ) {
    return null;
  }

  const safe = {
    installationId: value.installationId.toLowerCase(),
    currentDeviceReference: value.currentDeviceReference.toLowerCase(),
    provider: value.provider,
    platform: value.platform,
    appContext: value.appContext,
    permissionState: value.permissionState,
    enabled: value.enabled,
    refreshRequired: value.refreshRequired === true,
    providerTargetInvalidated: value.providerTargetInvalidated === true,
  };
  for (const field of OPTIONAL_METADATA_FIELDS) {
    if (value[field] !== null && typeof value[field] !== "string") return null;
    safe[field] = value[field];
  }
  return safe;
}

async function errorFromResponse(response, operation) {
  let detail = "";
  try {
    const payload = await response.json();
    detail = typeof payload?.detail === "string" ? payload.detail : "";
  } catch {
    detail = "";
  }
  return new PushRegistrationsApiError(
    STABLE_ERROR_CODES.has(detail)
      ? detail
      : "push_registration_unavailable",
    { operation, status: response.status }
  );
}

function invalidResponse(operation) {
  return new PushRegistrationsApiError("push_registration_unavailable", {
    operation,
  });
}

function invalidClaim(code = "push_registration_invalid") {
  return new PushRegistrationsApiError(code, { operation: "claim" });
}

function validTimezone(value) {
  if (value === null) return true;
  if (typeof value !== "string" || !value.trim() || value.length > 255) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("en", { timeZone: value.trim() }).format();
    return true;
  } catch {
    return false;
  }
}

function safeClaimMetadata(metadata) {
  if (!isObject(metadata)) throw invalidClaim();
  if (Object.keys(metadata).some((field) => !CLAIM_METADATA_FIELDS.has(field))) {
    throw invalidClaim();
  }
  if (!PLATFORMS.has(metadata.platform)) {
    throw invalidClaim("push_registration_platform_invalid");
  }
  if (!APP_CONTEXTS.has(metadata.appContext)) {
    throw invalidClaim("push_registration_context_invalid");
  }
  if (metadata.permissionState !== "granted") {
    throw invalidClaim("push_registration_permission_required");
  }
  if (!ACTIVE_LOCALES.has(metadata.locale)) {
    throw invalidClaim("push_registration_locale_invalid");
  }
  if (!validTimezone(metadata.timezone)) {
    throw invalidClaim("push_registration_timezone_invalid");
  }
  if (metadata.appVersion !== null || metadata.deviceLabel !== null) {
    throw invalidClaim();
  }
  return {
    platform: metadata.platform,
    appContext: metadata.appContext,
    permissionState: "granted",
    locale: metadata.locale,
    timezone: metadata.timezone === null ? null : metadata.timezone.trim(),
    appVersion: null,
    deviceLabel: null,
  };
}
async function executeRequest(path, options, operation) {
  let response;
  try {
    response = await fetch(
      `${API_BASE}${path}`,
      withApiCredentials(options)
    );
  } catch {
    throw invalidResponse(operation);
  }
  if (!response.ok) throw await errorFromResponse(response, operation);
  try {
    return await response.json();
  } catch {
    throw invalidResponse(operation);
  }
}

async function reserveMutationGeneration(
  installationId,
  { authToken, signal } = {}
) {
  const payload = await executeRequest(
    `/push/registrations/${encodeURIComponent(
      installationId
    )}/mutation-generation`,
    {
      method: "POST",
      headers: authorizationHeaders(authToken),
      signal,
    },
    "reserve-mutation"
  );
  if (
    !isObject(payload) ||
    !Number.isSafeInteger(payload.mutationGeneration) ||
    payload.mutationGeneration < 1
  ) {
    throw invalidResponse("reserve-mutation");
  }
  return payload.mutationGeneration;
}

export async function fetchPushRegistrations({ authToken, signal } = {}) {
  const payload = await executeRequest(
    "/push/registrations",
    {
      method: "GET",
      headers: authorizationHeaders(authToken),
      signal,
    },
    "list"
  );
  if (
    !isObject(payload) ||
    !Array.isArray(payload.registrations) ||
    !isSafeCount(payload.activeCount)
  ) {
    throw invalidResponse("list");
  }
  const registrations = payload.registrations.map(sanitizeRegistration);
  if (registrations.some((registration) => !registration)) {
    throw invalidResponse("list");
  }
  return { registrations, activeCount: payload.activeCount };
}

export async function claimPushRegistration(
  installationId,
  firebaseInstallationId,
  metadata,
  { authToken, signal, consent } = {}
) {
  const normalizedInstallationId = String(installationId || "")
    .trim()
    .toLowerCase();
  if (!isCanonicalPushInstallationId(normalizedInstallationId)) {
    throw invalidClaim("push_registration_installation_invalid");
  }
  if (!isValidFirebaseInstallationId(firebaseInstallationId)) {
    throw invalidClaim("push_registration_fid_invalid");
  }
  const safeMetadata = safeClaimMetadata(metadata);
  let safeConsent = {};
  if (consent !== undefined) {
    if (
      !isObject(consent) ||
      consent.version !== PUSH_ONBOARDING_CONSENT_VERSION ||
      consent.source !== PUSH_ONBOARDING_OPT_IN_SOURCE ||
      Object.keys(consent).some((field) => !["version", "source"].includes(field))
    ) {
      throw invalidClaim("push_consent_invalid");
    }
    safeConsent = {
      consentVersion: consent.version,
      optInSource: consent.source,
    };
  }
  const mutationGeneration = await reserveMutationGeneration(
    normalizedInstallationId,
    { authToken, signal }
  );
  const payload = await executeRequest(
    `/push/registrations/${encodeURIComponent(normalizedInstallationId)}`,
    {
      method: "PUT",
      headers: {
        ...authorizationHeaders(authToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firebaseInstallationId,
        mutationGeneration,
        ...safeMetadata,
        ...safeConsent,
      }),
      signal,
    },
    "claim"
  );
  const registration = sanitizeRegistration(payload?.registration);
  if (
    !isObject(payload) ||
    payload.ok !== true ||
    !registration ||
    registration.installationId !== normalizedInstallationId ||
    !isSafeCount(payload.activeCount) ||
    typeof payload.pushEnabled !== "boolean"
  ) {
    throw invalidResponse("claim");
  }
  return {
    ok: true,
    registration,
    activeCount: payload.activeCount,
    pushEnabled: payload.pushEnabled,
  };
}

export async function revokePushRegistration(
  installationId,
  { authToken, signal } = {}
) {
  const normalizedInstallationId = String(installationId || "")
    .trim()
    .toLowerCase();
  if (!isCanonicalPushInstallationId(normalizedInstallationId)) {
    throw new PushRegistrationsApiError(
      "push_registration_installation_invalid",
      { operation: "revoke" }
    );
  }
  const payload = await executeRequest(
    `/push/registrations/${encodeURIComponent(normalizedInstallationId)}`,
    {
      method: "DELETE",
      headers: authorizationHeaders(authToken),
      signal,
    },
    "revoke"
  );
  if (
    !isObject(payload) ||
    payload.ok !== true ||
    typeof payload.installationId !== "string" ||
    payload.installationId.toLowerCase() !== normalizedInstallationId ||
    !isSafeCount(payload.activeCount) ||
    typeof payload.pushEnabled !== "boolean"
  ) {
    throw invalidResponse("revoke");
  }
  return {
    ok: true,
    installationId: normalizedInstallationId,
    activeCount: payload.activeCount,
    pushEnabled: payload.pushEnabled,
  };
}

export async function revokeAllPushRegistrations({ authToken, signal } = {}) {
  const payload = await executeRequest(
    "/push/registrations/revoke-all",
    {
      method: "POST",
      headers: authorizationHeaders(authToken),
      signal,
    },
    "revoke-all"
  );
  if (
    !isObject(payload) ||
    payload.ok !== true ||
    !isSafeCount(payload.activeCount) ||
    typeof payload.pushEnabled !== "boolean"
  ) {
    throw invalidResponse("revoke-all");
  }
  return {
    ok: true,
    activeCount: payload.activeCount,
    pushEnabled: payload.pushEnabled,
  };
}
